import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';

import usePDPFabricFilters from 'data-hooks/usePDPFabricFilters';
import usePDPFullScreenClickEvent from 'data-hooks/useEvents/usePDPFullScreenClickEvent';
import useProductPrice from 'data-hooks/useProductPrice';
import usePDPBasicDetails from 'components/consumer/PDP/state/context/usePDPBasicDetails';
import useUpholsteryFilteredEvent from 'data-hooks/useEvents/useUpholsteryFilteredEvent';
import { PDP_FILTER_TYPES } from 'commons/constants';
import PDPSelectUpholsteryUI from './PDPSelectUpholsteryUI';
import usePDPOptions from '../state/context/usePDPOptions';

const PDPSelectUpholstery = ({
  isEditOrder = false,
  mode = 'pdp',
  onMoreMaterialsClick,
  optionsLoading,
  productId = null,
  slug,
  setMarxentSelectedFabric,
}) => {
  const { data: basicDetails } = usePDPBasicDetails();
  const { data: optionData } = usePDPOptions();
  const { data: priceData } = useProductPrice(productId, slug);

  const {
    loading,
    error,
    data: {
      allMaterials,
      filterType,
      filteredMaterials,
      optionName,
      selectedMaterial,
      selectedColor,
      upholsteryOptionId,
      disabledMaterialIds,
      isSelectedOptionsQuickship,
      selectedOptionsStockQty,
      allMaterialsLength,
      filterCounts,
      favMaterials,
      flashSaleOptionIds,
      isClearanceFabricAvailable,
    },
    actions: {
      setFilterType,
      setSelectedColor,
      onOptionValueSelection,
      onOptionValueHover,
      setOptionAsFav,
      removeOptionAsFav,
    },
  } = usePDPFabricFilters(slug, isEditOrder, {
    enableSelectedOptionsInitialization: false,
    shouldUpdateQueryParam: !isEditOrder,
    productInfo: { productId },
    target: 'PDPUpholstery',
  });

  useEffect(() => {
    if (selectedMaterial?.sku) {
      setMarxentSelectedFabric?.(selectedMaterial.sku.replace('-', ''));
    }
  }, [selectedMaterial?.sku, setMarxentSelectedFabric]);

  const { fullScreenClickEvent } = usePDPFullScreenClickEvent(
    basicDetails,
    optionData,
    priceData
  );

  const { upholsteryFilteredEvent } = useUpholsteryFilteredEvent(
    basicDetails,
    optionData,
    priceData,
    'PDP'
  );

  useEffect(() => {
    if (mode === 'quickview' && filterType !== PDP_FILTER_TYPES.POPULAR) {
      setFilterType(PDP_FILTER_TYPES.POPULAR);
    }
  }, [filterType, mode, setFilterType]);

  const fabricFavProps = useMemo(
    () => ({
      actions: {
        setOptionAsFav,
        removeOptionAsFav,
      },
      data: {
        favMaterials,
        favProductData: {
          name: basicDetails?.name,
          productId: basicDetails?.id,
          category: basicDetails?.type.name,
        },
        defaultToMaterial: allMaterials?.[0],
      },
    }),
    [
      allMaterials,
      basicDetails?.id,
      basicDetails?.name,
      basicDetails?.type.name,
      favMaterials,
      removeOptionAsFav,
      setOptionAsFav,
    ]
  );

  if (error) {
    return null;
  }

  return (
    <PDPSelectUpholsteryUI
      allMaterialsLength={allMaterialsLength}
      disabledMaterialIds={disabledMaterialIds}
      fabricFavProps={fabricFavProps}
      filterCounts={filterCounts}
      filterType={filterType}
      flashSaleOptionIds={flashSaleOptionIds}
      fullScreenClickEvent={fullScreenClickEvent}
      isEditOrder={isEditOrder}
      isSelectedOptionsQuickship={isSelectedOptionsQuickship}
      loading={loading || optionsLoading}
      materials={filteredMaterials}
      mode={mode}
      onMoreMaterialsClick={onMoreMaterialsClick}
      onOptionValueHover={onOptionValueHover}
      onOptionValueSelection={onOptionValueSelection}
      selectedColor={selectedColor}
      selectedMaterial={selectedMaterial}
      selectedOptionsStockQty={selectedOptionsStockQty}
      setFilterType={setFilterType}
      setSelectedColor={setSelectedColor}
      slug={slug}
      upholsteryFilteredEvent={upholsteryFilteredEvent}
      upholsteryOptionId={upholsteryOptionId}
      upholsteryOptionName={optionName}
      isClearanceFabricAvailable={isClearanceFabricAvailable}
    />
  );
};

PDPSelectUpholstery.propTypes = {
  isEditOrder: PropTypes.bool,
  mode: PropTypes.oneOf(['pdp', 'quickview']),
  onMoreMaterialsClick: PropTypes.func,
  optionsLoading: PropTypes.bool,
  productId: PropTypes.number,
  slug: PropTypes.string,
  setMarxentSelectedFabric: PropTypes.func,
};

export default PDPSelectUpholstery;
